// src/hooks/canvas/useThreeSetup.js
//
// THREE.js sahne, kamera, renderer ve OrbitControls ilk kurulumu.
// AutoCAD tarzi mouse imlecine dogru zoom iceriri.

import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { setGlobalSceneRef } from '../../../shared/coordinates/floorPlanOrigin';
import { store as appStore } from '../../../redux/store';

export default function useThreeSetup(canvasRef, scenesRef, onSceneReady) {
  const activeSceneRef = useRef(null);
  const controlsRef = useRef(null);
  const [threeState, setThreeState] = useState(null);

  useEffect(() => {
    const container = canvasRef.current;
    if (!container) return;

    const width = container.clientWidth;
    const height = container.clientHeight;

    const initialScene = new THREE.Scene();
    scenesRef.current = { mimari: initialScene };
    activeSceneRef.current = initialScene;

    const camera = new THREE.OrthographicCamera(
      width / -2, width / 2, height / 2, height / -2, -1000, 1000
    );
    camera.position.set(0, 0, 10);
    camera.lookAt(0, 0, 0);
    camera.layers.enable(0);
    camera.layers.enable(1);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(width, height);
    renderer.setClearColor(0x000000, 1);
    renderer.sortObjects = true;
    container.appendChild(renderer.domElement);

    const controls = new OrbitControls(camera, renderer.domElement);
    controlsRef.current = controls;
    controls.enableRotate = false;
    controls.enablePan = true;
    controls.enableZoom = false;
    controls.mouseButtons = {
      LEFT: null,
      MIDDLE: THREE.MOUSE.PAN,
      RIGHT: null
    };

    const zoomSpeed = 1.1;
    const handleWheel = (event) => {
      event.preventDefault();
      const rect = renderer.domElement.getBoundingClientRect();
      const mouseX = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      const mouseY = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      const beforeWorld = new THREE.Vector3(mouseX, mouseY, 0).unproject(camera);
      if (event.deltaY < 0) camera.zoom *= zoomSpeed;
      else camera.zoom /= zoomSpeed;
      camera.zoom = Math.max(0.01, Math.min(camera.zoom, 1000));
      camera.updateProjectionMatrix();
      const afterWorld = new THREE.Vector3(mouseX, mouseY, 0).unproject(camera);
      const delta = beforeWorld.clone().sub(afterWorld);
      camera.position.add(delta);
      controls.target.add(delta);
      camera.updateProjectionMatrix();
    };
    renderer.domElement.addEventListener('wheel', handleWheel, { passive: false });

    setThreeState({ scene: initialScene, camera, renderer });
    onSceneReady?.(initialScene);
    setGlobalSceneRef(initialScene);
    if (typeof window !== 'undefined') window.__scene = initialScene;

    // ⚡ ON-DEMAND RENDER: boştayken (input/değişiklik yok) her kare tüm sahneyi
    // çizmeyi bırak. Herhangi bir input, controls değişimi veya Redux dispatch
    // (= sahne mutasyonu) ~0.5sn'lik bir render penceresi açar. Böylece etkileşim
    // sırasında davranış aynı kalır, boşta CPU/GPU ~0 olur. Programatik/imperatif
    // değişiklikler window.__wakeRender() ile de pencere açabilir.
    const WAKE_MS = 500;
    let renderUntil = (typeof performance !== 'undefined' ? performance.now() : 0) + 4000; // ilk yükleme
    let rafId = null;
    const wakeRender = () => {
      renderUntil = (typeof performance !== 'undefined' ? performance.now() : 0) + WAKE_MS;
    };
    if (typeof window !== 'undefined') window.__wakeRender = wakeRender;

    const wakeEvents = ['pointermove', 'pointerdown', 'pointerup', 'wheel'];
    wakeEvents.forEach(ev => renderer.domElement.addEventListener(ev, wakeRender, { passive: true }));
    window.addEventListener('keydown', wakeRender);
    window.addEventListener('keyup', wakeRender);
    controls.addEventListener('change', wakeRender);
    const unsubscribeStore = appStore.subscribe(wakeRender); // her Redux değişimi → render

    const animate = () => {
      rafId = requestAnimationFrame(animate);
      controls.update();
      const now = (typeof performance !== 'undefined' ? performance.now() : 0);
      if (now < renderUntil) {
        renderer.render(activeSceneRef.current, camera);
      }
    };
    animate();

    return () => {
      if (rafId != null) cancelAnimationFrame(rafId);
      wakeEvents.forEach(ev => renderer.domElement.removeEventListener(ev, wakeRender));
      window.removeEventListener('keydown', wakeRender);
      window.removeEventListener('keyup', wakeRender);
      controls.removeEventListener('change', wakeRender);
      try { unsubscribeStore(); } catch (_) {}
      if (typeof window !== 'undefined' && window.__wakeRender === wakeRender) window.__wakeRender = null;
      renderer.domElement.removeEventListener('wheel', handleWheel);
      controls.dispose();
      container.removeChild(renderer.domElement);
    };
  }, []);

  return { threeState, setThreeState, activeSceneRef, controlsRef };
}
